interface PlanFeatures {
    downloads: string;
    mediaTypes: string[];
    videoTemplates: string[];
    tools: string[];
    aiFeatures: string[];
}

export const STARTER_PLAN = 'starter';
export const UNLIMITED_PRO_PLAN = 'unlimited-pro';
export const UNLIMITED_ALL_ACCESS_PLAN = 'unlimited-all-access';
export const BUSINESS_PLAN = 'business';
export const SMALL_BUSINESS_PLAN = 'small-business';
export const ESSENTIALS_PLAN = 'essentials';
export const ALL_ACCESS_PLAN = 'all-access';

// Mapping: Plan Code -> Plan Type (for direct lookup)
const PLAN_CODE_TO_TYPE: Record<string, string> = {
    '4000': STARTER_PLAN,
    '4010': STARTER_PLAN,
    '4200': UNLIMITED_PRO_PLAN,
    '4210': UNLIMITED_PRO_PLAN,
    '4100': UNLIMITED_ALL_ACCESS_PLAN,
    '4110': UNLIMITED_ALL_ACCESS_PLAN,
    // TODO: Check the actual plan code for business/ent plan
    '4500': BUSINESS_PLAN,
    '4510': BUSINESS_PLAN,
    // new plans
    '4015': ESSENTIALS_PLAN,
    '4030': ESSENTIALS_PLAN,
    '4016': ESSENTIALS_PLAN,
    '4031': ESSENTIALS_PLAN,
    '4115': ALL_ACCESS_PLAN,
    '4130': ALL_ACCESS_PLAN,
    '4116': ALL_ACCESS_PLAN,
    '4131': ALL_ACCESS_PLAN,
    '4215': SMALL_BUSINESS_PLAN,
    '4216': SMALL_BUSINESS_PLAN,
};

// Mapping: Plan Type -> Plan Codes (for seeing all codes per plan)
const PLAN_TYPE_TO_CODES = {
    [STARTER_PLAN]: ['4000', '4010'],
    [UNLIMITED_PRO_PLAN]: ['4200', '4210'],
    [UNLIMITED_ALL_ACCESS_PLAN]: ['4100', '4110'],
    [BUSINESS_PLAN]: ['4500', '4510'],
    [ESSENTIALS_PLAN]: ['4015', '4030', '4016', '4031'],
    [ALL_ACCESS_PLAN]: ['4115', '4130', '4116', '4131'],
    [SMALL_BUSINESS_PLAN]: ['4215', '4216'],
} as const;

const starterBase: PlanFeatures = {
    aiFeatures: [],
    downloads: '5 Total Monthly Downloads',
    mediaTypes: ['HD footage', 'Music', 'Photos, vectors, & illustrations'],
    videoTemplates: [],
    tools: ['Maker'],
};

const essentialsBase: PlanFeatures = {
    aiFeatures: ['3,000 credits/month', 'Video Editing', 'Video Generation', 'Image Generation'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Video templates', 'Photos, vectors, & illustrations'],
    videoTemplates: [],
    tools: ['Maker'],
};

const essentials10xBase: PlanFeatures = {
    ...essentialsBase,
    aiFeatures: ['30,000 credits/month', 'Video Editing', 'Video Generation', 'Image Generation'],
};

const allAccessBase: PlanFeatures = {
    aiFeatures: ['4,500 credits/month', 'Video Editing', 'Voiceover', 'Video Generation', 'Image Generation'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const allAccess10xBase: PlanFeatures = {
    ...allAccessBase,
    aiFeatures: ['45,000 credits/month', 'Video Editing', 'Voiceover', 'Video Generation', 'Image Generation'],
};

const smallBusinessBase: PlanFeatures = {
    aiFeatures: ['6,000 credits/month', 'Video Editing', 'Voiceover', 'Video Generation', 'Image Generation'],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker', 'Admin controls'],
};

const smallBusiness10xBase: PlanFeatures = {
    ...smallBusinessBase,
    aiFeatures: ['60,000 credits/month', 'Video Editing', 'Voiceover', 'Video Generation', 'Image Generation'],
};

const unlimitedProBase: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const unlimitedAllAccessBase: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker'],
};

const businessBase: PlanFeatures = {
    aiFeatures: [],
    downloads: 'Unlimited Downloads',
    mediaTypes: ['8K, 4K, & HD footage', 'Music & sound effects', 'Photos, vectors & illustrations'],
    videoTemplates: ['After Effects', 'Premiere Pro', 'Apple Motion', 'DaVinci Resolve'],
    tools: ['Adobe Creative Cloud plugin', 'Maker', 'Admin controls'],
};

const plansFeatures: Record<string, PlanFeatures> = {
    '4000': starterBase,
    '4010': starterBase,
    '4200': unlimitedProBase,
    '4210': unlimitedProBase,
    '4100': unlimitedAllAccessBase,
    '4110': unlimitedAllAccessBase,
    '4500': businessBase,
    '4510': businessBase,
    '4015': essentialsBase,
    '4030': essentialsBase,
    '4016': essentials10xBase,
    '4031': essentials10xBase,
    '4115': allAccessBase,
    '4130': allAccessBase,
    '4116': allAccess10xBase,
    '4131': allAccess10xBase,
    '4215': smallBusinessBase,
    '4216': smallBusiness10xBase,
};

export const getPlanFeatures = (planCode: string): PlanFeatures | undefined => {
    return plansFeatures[planCode];
};

// Helper function to get plan type from plan code
export const getPlanTypeFromCode = (planCode: string): string => {
    return PLAN_CODE_TO_TYPE[planCode] || STARTER_PLAN;
};

// Helper function to get plan codes for a plan type
export const getPlanCodesForType = (planType: string): readonly string[] => {
    return PLAN_TYPE_TO_CODES[planType as keyof typeof PLAN_TYPE_TO_CODES] || [];
};

// Export the mappings for reference
export { PLAN_CODE_TO_TYPE, PLAN_TYPE_TO_CODES };

// Plan codes for All Access and Essentials plans
export const ENHANCED_CALLOUT_PLAN_CODES = new Set(['4115', '4130', '4015', '4030']);
